<template>

  <div class="cf nau-client-view">
    <div class="cf-page cf-stack cf-stack--8">

      <!-- ================= PROFIL ================= -->
      <div class="cf-row cf-row--split">
        <div class="cf-row">
          <span class="cf-avatar">{{ initials }}</span>
          <div>
            <h1 class="cf-h2">{{ user.name }} {{ user.surname }}</h1>
            <p class="cf-meta" style="margin:2px 0 0">{{ userMeta }}</p>
          </div>
        </div>
        <div class="cf-row">
          <button v-if="dataLoaded && nextLoanButton && !toContinue" class="cf-btn cf-btn--ghost" @click="nextLoan">Kolejna pożyczka</button>
          <button class="cf-btn cf-btn--quiet" @click="doPassChange">Zmień hasło</button>
        </div>
      </div>


      <!-- dane profilu -->
      <dl class="cf-dl cf-profile">
        <div class="cf-dl__row"><dt class="cf-dl__key">E-mail</dt><dd class="cf-dl__val">{{ user.email }}</dd></div>
        <div class="cf-dl__row"><dt class="cf-dl__key">Telefon</dt><dd class="cf-dl__val">{{ user.cellphone }}</dd></div>
        <div class="cf-dl__row"><dt class="cf-dl__key">Adres</dt><dd class="cf-dl__val">{{ userAddress }}</dd></div>
        <div class="cf-dl__row"><dt class="cf-dl__key">PESEL</dt><dd class="cf-dl__val">{{ user.pesel }}</dd></div>
      </dl>


      <!-- porzucony wniosek - karta wezwania do dzialania -->
      <div v-if="continueLoan" class="cf-card cf-card--action">
        <div class="cf-card__body cf-row cf-row--split" style="gap:36px">
          <div class="cf-stack" style="flex:1;min-width:min(440px,100%);gap:12px">
            <span class="cf-badge cf-badge--action">Do dokończenia</span>
            <h2 class="cf-h2">Masz rozpoczęty wniosek<template v-if="continueLoan.pay_out_ammount"> na {{ money(continueLoan.pay_out_ammount) }}</template></h2>
            <p class="cf-lead" style="margin:0">Wniosek nie został jeszcze zakończony. Wróć do niego, aby dokończyć proces
              — zaczniesz od miejsca, w którym przerwałeś.</p>
          </div>
          <div class="cf-stack" style="gap:12px;min-width:260px">
            <button class="cf-btn cf-btn--primary cf-btn--block" @click="goContinue(continueLoan.uniqueid, continueLoan.id, continueLoan)">Dokończ wniosek</button>
            <p class="cf-meta" style="margin:0;text-align:center">Zajmie to tylko chwilę</p>
          </div>
        </div>
      </div>


      <!-- zlozony wniosek w rozpatrywaniu (status 1.00 "Zaakceptowana" tuz po zlozeniu) -
           informacja zamiast przycisku "Kolejna pozyczka" (jak w CardFinance2) -->
      <aside v-if="pendingApplication" class="cf-note">
        <span class="cf-note__count">i</span>
        <div class="cf-stack" style="gap:6px">
          <span class="cf-note__title">Analizujemy Twój wniosek.</span>
          <span class="cf-note__hint">Otrzymaliśmy komplet danych i trwa ich analiza. O decyzji poinformujemy Cię SMS-em
            i e-mailem — zwykle zajmuje to kilkanaście minut w godzinach pracy biura. Do tego czasu nie można złożyć
            kolejnego wniosku.</span>
        </div>
      </aside>


      <!-- pozyczka przekazana do wyplaty -->
      <aside v-if="showProcessingInfo" class="cf-note">
        <span class="cf-note__count">i</span>
        <div class="cf-stack" style="gap:6px">
          <span class="cf-note__title">Twoja pożyczka została przekazana do wypłaty.</span>
          <span class="cf-note__hint">Potwierdzenie wypłaty prześlemy SMS-em. W razie pytań zapraszamy do kontaktu z infolinią 22 696 72 70.</span>
        </div>
      </aside>


      <p v-if="dataLoaded && !loans.length && !nextLoanButton && !pendingApplication" class="cf-lead" style="margin:0">
        Aktualnie nie posiadamy dla Ciebie oferty kolejnej pożyczki.
      </p>


      <!-- ================= UMOWY ================= -->
      <div class="cf-stack cf-stack--4">

        <div class="cf-row cf-row--split">
          <h2 class="cf-section-title">Twoje umowy</h2>
          <span class="cf-meta">{{ loans.length }} {{ loans.length == 1 ? 'pozycja' : loans.length < 5 && loans.length > 0 ? 'pozycje' : 'pozycji' }}</span>
        </div>


        <p v-if="dataLoaded && !loans.length" class="cf-lead" style="margin:0">Nie masz jeszcze żadnych umów.</p>


        <article v-for="l in loans" :key="l.id" class="cf-card cf-contract"
                 :class="{ 'cf-card--muted': isClosed(l), 'cf-card--action': isContinue(l) }">

          <div>
            <div class="cf-contract__no" :style="isClosed(l) ? 'color:var(--cf-ink-2)' : ''">{{ l.contract_nbr }}</div>
            <div class="cf-contract__desc">{{ l.template_name || 'Pożyczka' }}</div>
          </div>

          <div>
            <div class="cf-contract__label">Data</div>
            <div :style="isClosed(l) ? 'color:var(--cf-ink-2)' : ''">{{ l.loan_date }}</div>
          </div>

          <div>
            <div class="cf-contract__label">Kwota</div>
            <div class="cf-contract__value" :style="isClosed(l) ? 'color:var(--cf-ink-2)' : ''">{{ money(l.pay_out_ammount) }}</div>
          </div>

          <div class="cf-row" style="gap:18px">
            <span class="cf-status" :class="statusOf(l).cls">{{ statusOf(l).label }}</span>

            <button v-if="isContinue(l)" class="cf-link-action" @click="goContinue(l.uniqueid, l.id, l)">Dokończ wniosek →</button>

            <button v-else class="cf-link-action" :style="isClosed(l) ? 'color:var(--cf-ink-4)' : ''"
                    @click="toggleDetails(l)">{{ expanded[l.id] ? 'Zwiń' : (isClosed(l) ? 'Dokumenty' : 'Szczegóły') }}</button>
          </div>


          <!-- ================= SZCZEGOLY ================= -->
          <div v-if="expanded[l.id]" class="cf-contract__details cf-stack" style="gap:20px">

            <dl class="cf-dl">
              <div class="cf-dl__row">
                <dt class="cf-dl__key">Status</dt><dd class="cf-dl__val">{{ isPending(l) ? 'Wniosek w rozpatrywaniu' : isClosed(l) ? 'Spłacona' : 'Aktywna' }}</dd>
              </div>

              <!-- wniosek w rozpatrywaniu: parametry wnioskowanej pozyczki, bez danych do splaty -->
              <template v-if="isPending(l)">
                <div v-if="l.template_name" class="cf-dl__row">
                  <dt class="cf-dl__key">Produkt</dt><dd class="cf-dl__val">{{ l.template_name }}</dd>
                </div>
                <div class="cf-dl__row">
                  <dt class="cf-dl__key">Wnioskowana kwota</dt><dd class="cf-dl__val">{{ money(l.pay_out_ammount) }}</dd>
                </div>
                <div v-if="l.installments_nbr" class="cf-dl__row">
                  <dt class="cf-dl__key">Okres</dt><dd class="cf-dl__val">{{ l.installments_nbr }} {{ String(l.installments_period).match(/^d/) ? 'dni' : 'rat' }}</dd>
                </div>
                <div v-if="l.installment_amount" class="cf-dl__row">
                  <dt class="cf-dl__key">Rata</dt><dd class="cf-dl__val">{{ money(l.installment_amount) }}</dd>
                </div>
                <div class="cf-dl__row cf-dl__row--pending">
                  <dt class="cf-dl__key">Decyzja</dt><dd class="cf-dl__val"><span class="cf-pending">W analizie — poinformujemy Cię SMS-em i e-mailem</span></dd>
                </div>
              </template>

              <template v-else-if="!isClosed(l)">
                <div v-if="l.template_name" class="cf-dl__row">
                  <dt class="cf-dl__key">Produkt</dt><dd class="cf-dl__val">{{ l.template_name }}</dd>
                </div>
                <div v-if="l.incoming_payment" class="cf-dl__row">
                  <dt class="cf-dl__key">Termin spłaty raty</dt><dd class="cf-dl__val">{{ fmtDate(l.incoming_payment) }}</dd>
                </div>
                <div class="cf-dl__row">
                  <dt class="cf-dl__key">Odbiorca przelewu</dt>
                  <dd class="cf-dl__val cf-copy" @click="copyToClipboard">Nauczycielska Agencja Ubezpieczeniowa S.A. <i class="fa fa-copy cf-copy-icon"></i></dd>
                </div>
                <div v-if="l.loan_bank_account" class="cf-dl__row">
                  <dt class="cf-dl__key">Nr rachunku do przelewu</dt>
                  <dd class="cf-dl__val cf-copy" @click="copyToClipboard">{{ l.loan_bank_account }} <i class="fa fa-copy cf-copy-icon"></i></dd>
                </div>
                <div class="cf-dl__row">
                  <dt class="cf-dl__key">Tytuł przelewu</dt>
                  <dd class="cf-dl__val cf-copy" @click="copyToClipboard">spłata do umowy nr. {{ l.contract_nbr }} <i class="fa fa-copy cf-copy-icon"></i></dd>
                </div>
                <div class="cf-dl__row">
                  <dt class="cf-dl__key">Kwota raty</dt>
                  <dd class="cf-dl__val cf-copy" @click="copyToClipboard">{{ money(installmentOf(l)) }} <i class="fa fa-copy cf-copy-icon"></i></dd>
                </div>
                <div class="cf-dl__row">
                  <dt class="cf-dl__key">Pozostała całkowita kwota do spłaty</dt>
                  <dd class="cf-dl__val cf-copy" @click="copyToClipboard">{{ money(l.to_be_paid) }} <i class="fa fa-copy cf-copy-icon"></i></dd>
                </div>
              </template>

              <div v-if="l.schedule && l.schedule.length > 0 && !isPending(l)" class="cf-dl__row">
                <dt class="cf-dl__key">Aktualny harmonogram</dt>
                <dd class="cf-dl__val"><a href="#" @click.prevent="showSchedule(l.schedule)">Harmonogram spłat</a></dd>
              </div>
            </dl>


            <div v-if="(docsByLoan[l.id] || []).length" class="cf-stack" style="gap:10px">
              <span class="cf-eyebrow cf-eyebrow--muted">Do pobrania</span>
              <a v-for="doc in docsByLoan[l.id]" :key="doc.id" class="cf-doc-link"
                 @click="download(doc.id, doc.document_name)">
                <i class="fas fa-download"></i> {{ doc.document_name }}
              </a>
            </div>

          </div>

        </article>

      </div>

    </div>


    <!-- Modal: harmonogram -->
    <div class="modal fade" ref="modal">
      <div class="modal-dialog modal-dialog-scrollable modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header container dark-bg">
            <div class="col-auto me-auto ">
              <h3>Harmonogram spłat</h3>
            </div>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">

            <table class="cf-schedule">
              <thead>
                <tr><th>Data raty</th><th>Kwota raty</th><th>Wpłacono</th><th>Pozostało</th></tr>
              </thead>
              <tbody>
                <tr v-for="r in schedule" :key="r.date">
                  <td>{{ r.date }}</td>
                  <td>{{ r.amount }}</td>
                  <td>{{ r.paid }}</td>
                  <td>{{ (r.amount - r.paid).toFixed(2) }}</td>
                </tr>
              </tbody>
            </table>

          </div>
          <div class="modal-footer justify-content-center">
            <button type="button" class="cf-btn cf-btn--primary" data-bs-dismiss="modal"> Zamknij </button>
          </div>
        </div>
      </div>
    </div>


    <PassChange />

  </div>

</template>

<script>

  import PassChange from '@/components/PassChange.vue';


  // ---------------------------------------------------------------------------
  // Strefa klienta po zalogowaniu - redesign przeniesiony z CardFinance2:
  // profil z awatarem, karta wezwania "Dokoncz wniosek" (porzucony wniosek,
  // np. status EasyCheck got 0.13), lista umow jako karty cf-contract zamiast
  // b-table (statusy jako badge, szczegoly rozwijane w karcie).
  // Logika danych bez zmian wzgledem ZetosClientView: GET /client/loans,
  // dokumenty per pozyczka, harmonogram, dokonczenie wniosku (resume).
  // ---------------------------------------------------------------------------

  export default {

    name: 'NauClientView',

    components: {
      PassChange
    },


    data() {
      return {

        nextLoanButton: false,

        // wniosek porzucony do dokonczenia (np. etap wyboru konta) - flaga ukrywa "Kolejna Pozyczka"
        toContinue: false,

        // przycisk "Kolejna Pozyczka" odslaniamy dopiero po zaladowaniu listy wnioskow (znamy toContinue)
        dataLoaded: false,

        showProcessingInfo: false,

        // zlozony wniosek w rozpatrywaniu (status 1.00) - komunikat, blokada kolejnego wniosku
        pendingApplication: false,

        user: {},

        loans: [],

        // { <loan_id>: true } - rozwiniete karty
        expanded: {},

        // { <loan_id>: [dokumenty] }
        docsByLoan: {},

        schedule: [],

        modal: null
      };
    },


    computed: {

      initials(){

        return ((this.user.name || ' ')[0] + (this.user.surname || ' ')[0]).toUpperCase();
      },


      userAddress(){

        let line = [this.user.street1, this.user.building1].filter(Boolean).join(' ') + (this.user.flat1 ? '/' + this.user.flat1 : ''),
            city = [this.user.postal_code1, this.user.city1].filter(Boolean).join(' ');

        return [line, city].filter(s => s && s.trim()).join(', ');
      },


      userMeta(){

        return [this.user.email, this.user.cellphone].filter(Boolean).join(' · ');
      },


      // porzucony wniosek do dokonczenia - karta wezwania nad lista
      continueLoan(){

        return this.loans.find(l => this.isContinue(l));
      }
    },


    methods: {

      isClosed(l){

        return !!parseInt(l.is_closed);
      },


      isContinue(l){

        return parseFloat(l.to_be_continied) == 1;
      },


      // zlozony wniosek w rozpatrywaniu: status 1.00 "Zaakceptowana" (tuz po
      // zlozeniu, przed 1.05 = dokumenty / umowa). Nie liczy sie porzucony
      // wniosek do dokonczenia ani rekord zamkniety
      isPending(l){

        const sid = parseFloat(l.status_id);

        return !this.isContinue(l) && !this.isClosed(l) && sid >= 1 && sid < 1.05;
      },


      statusOf(l){

        if (this.isContinue(l)) {
          return { label: 'Wniosek — do dokończenia', cls: 'cf-status--todo' };
        }

        if (this.isClosed(l)) {
          return { label: 'Spłacona', cls: 'cf-status--closed' };
        }

        if (this.isPending(l)) {
          return { label: 'Wniosek — w rozpatrywaniu', cls: 'cf-status--info' };
        }

        if (parseFloat(l.status_id) == 1.05) {
          return { label: 'Wniosek złożony', cls: 'cf-status--info' };
        }

        if (parseFloat(l.status_id) == 1.1) {
          return { label: 'Podpisana — w wypłacie', cls: 'cf-status--info' };
        }

        return { label: 'Aktywna', cls: 'cf-status--info' };
      },


      installmentOf(l){

        return Math.max(parseFloat(l.to_be_paid_now) || 0, parseFloat(l.incoming_payment_amount) || 0);
      },


      money(v){

        let n = parseFloat(String(v == null ? '' : v).replace(/\s/g, '').replace(',', '.'));

        return isNaN(n) ? '' : this.formatCurrency(n, false) + ' zł';
      },


      fmtDate(v){

        return v && String(v).split(' ')[0].replace(/([0-9]{4})-([0-9]{2})-([0-9]{2})/, '$3.$2.$1');
      },


      toggleDetails(l){

        this.$set(this.expanded, l.id, !this.expanded[l.id]);

        this.expanded[l.id] && !this.docsByLoan[l.id] && this.getDocs(l.id);
      },


      doPassChange(){

        this.echo('pass_change_dialog');
      },


      nextLoan() {

        this.$router.push({path: '/fullform'});

        this.pageTop();
      },


      // odtworz parametry pozyczki (kalkulator) z danych wiersza wniosku
      applyRowData(data){

        this.loanAmount = parseInt(data.pay_out_ammount);

        this.tenorSign = String(data.installments_period).slice(-1);

        this.loanTenor = parseInt(data.installments_nbr) || parseInt(data.loan_days);

        data.last_installment_date
          && (this.finishDate = data.last_installment_date.split(' ')[0].replace(/([0-9]{4})-([0-9]{2})-([0-9]{2})/, '$3.$2.$1'));
      },


      // czy wiersz z /client/loans jest w statusie traktowanym jak porzucony wniosek
      // do dokonczenia (config: continue_statuses). Wpisy liczbowe dopasowywane po
      // parseFloat ze status_id (0.13 == '.13' == '0.1300'), tekstowe po status_name
      isContinueStatus(l) {

        return (this.getConf('continue_statuses') || []).some( s => {

          s = String(s).toLowerCase();

          return !isNaN(parseFloat(s))
              ? parseFloat(s) == parseFloat(l.status_id)
              : s == String(l.status_name).toLowerCase();
        });
      },


      // kontynuacja porzuconego wniosku - wznow po stronie API i przekieruj wg zwroconego kroku
      goContinue(uid, loanId, rowData) {

        this.log('APPLICATION_CONTINUE', 'uid=' + uid, 'loanId=' + loanId, 'leadId=' + (rowData && rowData.ireq_id));

        var page = this.$root.mainpage,
            dat,
            step;

        // id wniosku internetowego (internet_loan_request_id) - po nim ekran wyboru
        // rachunku pobiera konta z analizy (GET /application/bank/<ireq_id>), bo sesja
        // backendu po wznowieniu z panelu nie ma application_id
        page.leadId = rowData && rowData.ireq_id ? rowData.ireq_id : null;

        page.isLoading = true;

        this.get('/application/resume/' + uid)

          .then(response => {

            dat = Array.isArray(response.data) && response.data.length > 0 ? {...response.data[0]} : response.data;

            step = dat && dat.hasOwnProperty('step') ? dat.step : '';

            this.log('CONTINUE_RESPONSE', 'step=' + step);

            switch (step) {

              case 'wait':
                page.isLoading = false;

                // wniosek przerwany na wyborze konta (np. 'EasyCheck got' 0.13): step='account'
                // dostaje dopiero status 0.50, wiec resume zwraca 'wait' - wracamy wprost
                // na ekran wyboru konta zamiast na strone oczekiwania
                if (this.isContinueStatus(rowData)) {

                  page.loanId = loanId;
                  this.applyRowData(rowData);
                  this.$router.push({path: 'account'});
                  return;
                }

                this.$router.push({path: 'pending'});
                return;


              case 'counteroffer':
                page.isLoading = false;

                if (dat.last_installment_date) {
                  this.finishDate = dat.last_installment_date.split(' ')[0].replace(/([0-9]{4})-([0-9]{2})-([0-9]{2})/, '$3.$2.$1');
                }

                this.setValues( parseInt(dat.net_amount || rowData.pay_out_ammount), parseInt(dat.installments_nbr) || this.getConf('default_tenor'), this.getConf('tenor_sign'), this.finishDate );

                // NauProposalView w mounted() odsyla na /client gdy aisdone nie jest ustawione
                this.$root.mainpage.aisdone = true;

                this.$router.push({path: 'proposal'});
                return;


              case 'account':
                page.isLoading = false;
                page.loanId = loanId;

                this.applyRowData(rowData);

                this.$router.push({path: 'account'});
                return;


              case 'amodit':
                page.isLoading = false;

                let url = dat.step_details.split('/');

                window.location = this.getConf('amoditSignUrl') + url[ url.length - 2 ] + '%2F' + url[ url.length - 1 ];
                return;


              case 'authologic_start':
              case 'easycheck':
                page.isLoading = false;
                window.location = dat.step_details;
                return;


              case 'reject':
                page.isLoading = false;
                this.$router.push({path: 'rej'});
                return;


              case 'accept':
                page.isLoading = false;
                page.loanId = loanId;
                this.$router.push({path: 'thankyou'});
                return;


              default:
                page.isLoading = false;

                // resume potrafi zwrocic pusty step (getLoanCurrentStep odfiltrowuje 'wait') -
                // dla wniosku w statusie kontynuacji rowniez kierujemy na wybor konta
                if (this.isContinueStatus(rowData)) {

                  page.loanId = loanId;
                  this.applyRowData(rowData);
                  this.$router.push({path: 'account'});
                  return;
                }

                this.$router.push({path: 'pending'});
                return;
            }

          })
          .catch(error => {

            page.isLoading = false;

            this.log('CONTINUE_ERROR', error && error.message ? error.message : 'no-msg');
          });
      },


      download(downloadID, name) {

        let fileLink = document.createElement('a');

        fileLink.href = '/api/client/document/' + downloadID;
        fileLink.setAttribute('download', String(name).trim() + '.pdf');
        document.body.appendChild(fileLink);

        fileLink.click();
      },


      getDocs(loanId){

        this.get('/client/documents/' + loanId)
          .then(response => {

            let seen = [],
                docs = !response.data ? [] : response.data.filter( doc =>
                  seen.indexOf(doc.document_name) == -1
                  && String(doc.document_name).indexOf('WZOR') == -1
                  && seen.push(doc.document_name) );

            this.$set(this.docsByLoan, loanId, docs);
          })
          .catch(error => {
            console.log('Network error', error);
          });
      },


      showSchedule(data){

        this.schedule = (data || []).map( el => { let o = el.split(':'); return { date: o[0], amount: o[1], paid: o[2] }; });

        this.modal.show();
      },


      loadLoans() {

        this.get('/client/loans')

          .then(response => {

            let all = response.data || [],
                skipCounter = 0;

            this.toContinue = false;

            all.map( l => {

              // status z listy continue_statuses (np. 'EasyCheck got' 0.13 - klient przerwal
              // na ekranie wyboru konta) -> wymus flage kontynuacji
              parseFloat(l.to_be_continied) != 1
                && this.isContinueStatus(l)
                && (l.to_be_continied = '1');

              parseFloat(l.status_id) == 1.1 && (this.showProcessingInfo = true);

              !this.toContinue && parseFloat(l.to_be_continied) > 0 && (this.toContinue = true);

              !!l.template_name && l.template_name.match(/13-stka/) && skipCounter++;
            });


            // zlozony wniosek w rozpatrywaniu - komunikat zamiast przycisku "Kolejna pozyczka"
            this.pendingApplication = all.some(l => this.isPending(l));


            // allow next loan (ukryj gdy istnieje porzucony wniosek do dokonczenia lub wniosek w analizie)
            this.nextLoanButton = !this.toContinue && !this.pendingApplication && !parseInt(this.user.rejected_loan_no) && parseInt(this.user.active_loan_no) <= skipCounter;


            this.loans = all.filter( l => {

              const sid = parseFloat(l.status_id);

              // pokaz aktywne/wniosek/podpisane, wnioski w rozpatrywaniu oraz porzucone do dokonczenia
              return ((sid >= 3 || sid == 1.05) && !parseInt(l.is_closed)) || parseFloat(l.to_be_continied) == 1 || this.isPending(l);

            }).map( l => {

              l.schedule = !l.pending_installments ? [] : l.pending_installments.split(',');

              l.loan_date = this.fmtDate(l.loan_date);

              l.pay_out_ammount = parseInt(l.pay_out_ammount);

              return l;
            });


            this.dataLoaded = true;
          })
          .catch(error => {

            console.log('Network error', error);

            this.dataLoaded = true;
          });
      }
    },


    created() {

      this.user = JSON.parse(localStorage.getItem('user')) || {};

      // no active loans and no rejections so activate next loan button
      this.nextLoanButton = !this.toContinue && !parseInt(this.user.rejected_loan_no) && !parseInt(this.user.active_loan_no);

      this.loadLoans();
    },


    mounted() {

      this.modal = new bootstrap.Modal(this.$refs['modal']);
    }
  }

</script>


<style scoped>

/* struktura i tokeny w src/assets/cf-theme.css; tu tylko drobiazgi widoku */

.cf-page {
  padding: 44px 64px 56px;
}

.cf-profile {
  border: 1px solid var(--cf-line);
  border-radius: var(--cf-r-card);
  padding: 8px 28px;
}

.cf-doc-link {
  font-size: 14px;
  cursor: pointer;
}

.cf-copy {
  cursor: pointer;
}

.cf-copy-icon {
  color: var(--cf-teal-deep);
  padding-left: .5rem;
}

.cf-schedule {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.cf-schedule th {
  text-align: left;
  font-size: 11.5px;
  letter-spacing: .1em;
  text-transform: uppercase;
  color: var(--cf-ink-5);
  padding: 8px 10px;
  border-bottom: 1px solid var(--cf-line);
}

.cf-schedule td {
  padding: 10px;
  border-bottom: 1px solid var(--cf-line-soft);
}

.modal-dialog {
  max-width: 720px;
}

@media (max-width: 900px) {

  .cf-page {
    padding: 24px 18px 40px;
  }

  .cf-profile {
    padding: 4px 16px;
  }
}

</style>



// WEBPACK FOOTER //
// src/NauClientView.vue